/**
 * NumericInput Component
 *
 * Number input with validation, formatting, and optional units.
 */

import React, { useState, useEffect } from 'react';
import type { ValidationMessage } from '../../types/validation';

export interface NumericInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  note?: string;
  errors?: ValidationMessage[];
  warnings?: ValidationMessage[];
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
}

export const NumericInput: React.FC<NumericInputProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  unit,
  note,
  errors = [],
  warnings = [],
  disabled = false,
  required = false,
  placeholder,
  className = '',
}) => {
  const [localValue, setLocalValue] = useState(value.toString());
  const hasError = errors.length > 0;
  const hasWarning = warnings.length > 0;

  useEffect(() => {
    setLocalValue(value.toString());
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    const numValue = parseFloat(newValue);
    if (!isNaN(numValue)) {
      onChange(numValue);
    }
  };

  const handleBlur = () => {
    const numValue = parseFloat(localValue);
    if (isNaN(numValue)) {
      setLocalValue(value.toString());
    } else {
      // Apply min/max constraints
      let constrainedValue = numValue;
      if (min !== undefined && constrainedValue < min) constrainedValue = min;
      if (max !== undefined && constrainedValue > max) constrainedValue = max;

      if (constrainedValue !== numValue) {
        onChange(constrainedValue);
        setLocalValue(constrainedValue.toString());
      }
    }
  };

  return (
    <div className={`mb-3 ${className}`}>
      <label className="block text-xs font-semibold text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative">
        <input
          type="number"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          placeholder={placeholder}
          className={`
            w-full px-3 py-2 text-sm border rounded
            ${hasError ? 'border-red-500 focus:ring-red-500' : ''}
            ${hasWarning && !hasError ? 'border-yellow-500 focus:ring-yellow-500' : ''}
            ${!hasError && !hasWarning ? 'border-gray-300 focus:ring-indigo-500' : ''}
            focus:outline-none focus:ring-2
            disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed
            ${unit ? 'pr-12' : ''}
          `}
        />
        {unit && (
          <span className="absolute right-3 top-2 text-xs text-gray-500 font-mono">
            {unit}
          </span>
        )}
      </div>

      {note && !hasError && !hasWarning && (
        <div className="text-xs text-gray-500 mt-1">{note}</div>
      )}

      {errors.map((error, idx) => (
        <div key={idx} className="text-xs text-red-600 mt-1">
          {error.message}
        </div>
      ))}

      {warnings.map((warning, idx) => (
        <div key={idx} className="text-xs text-yellow-600 mt-1">
          {warning.message}
        </div>
      ))}
    </div>
  );
};
